import { useStats, useStatsT } from './statsContext'
import { Delta, Description, ErrorLine, ListBlock, Loading, Pills, StatusIcon, TableHead, UserCell } from './StatsParts'
import { StatsChart } from './StatsChart'
import {
  EARNING_TYPES,
  earningsChartQuery,
  formatDate,
  formatDateTime,
  invoiceUrl,
  listKeyOf,
  money,
  offsetLoader,
  PAGE_SIZE,
  payoutChartQuery,
  PILLS,
  plain,
  transactionsLoader,
  withRange,
  type Raw,
} from './statisticsData'
import { useStat, useStatList } from './statsStore'

function EarningStatus({ row }: { row: Raw }) {
  const { t } = useStatsT()
  const status = String(row.status || '').toLowerCase()
  if (status === 'loading') {
    const days = Number(row.payoutPendingDays)
    return <StatusIcon icon="clock" tone="pending" label={Number.isFinite(days) && days > 0 ? t('earningPending', { n: days }) : t('earningIsPending')} />
  }
  if (status === 'done') return <StatusIcon icon="check-circle" tone="done" label={t('complete')} />
  if (status === 'undo') {
    const reason = plain(row.refundReason)
    return <StatusIcon icon="undo-2" tone="reversed" label={reason ? t('reversedReason', { reason }) : t('reversed')} />
  }
  return status ? <span className="ofx-status-text">{status}</span> : null
}

function PayoutState({ row }: { row: Raw }) {
  const { t } = useStatsT()
  const state = String(row.state || '').toLowerCase()
  if (state === 'approved')
    return (
      <>
        <span className="ofx-state">{t('payoutProcessed')}</span>
        <StatusIcon icon="check-circle" tone="done" label={t('processed')} />
      </>
    )
  if (state === 'rejected') {
    const reason = plain(row.rejectReason)
    return (
      <>
        <span className="ofx-state is-rejected">
          {t('payoutReturned')}
          {reason && <span className="ofx-state-reason">{t('rejectReason', { reason })}</span>}
        </span>
        <StatusIcon icon="alert-circle" tone="rejected" label={t('rejected')} />
      </>
    )
  }
  if (state === 'new' || state === 'pending')
    return (
      <>
        <span className="ofx-state">{t('payoutProcessing')}</span>
        <StatusIcon icon="clock" tone="pending" label={t('processing')} />
      </>
    )
  return <span className="ofx-state">{state || '—'}</span>
}

/** Payout / chargeback / referral charts: total, change and Amount over Count. */
function PayoutChart({ path }: { path: string }) {
  const { t } = useStatsT()
  const { store, range } = useStats()
  const entry = useStat(store, path, payoutChartQuery(range, path))
  if (entry.status === 'loading') return <Loading />
  if (entry.status === 'error') return <ErrorLine message={entry.error} />
  const d = entry.data ?? {}
  return (
    <>
      <div className="ofx-chart-head">
        <strong>{money(d.total)}</strong>
        <Delta value={d.delta} />
      </div>
      <StatsChart main={{ points: d.chartAmount, kind: 'money', label: t('l.amount') }} sub={{ points: d.chartCount, kind: 'count', label: t('l.count') }} />
    </>
  )
}

export function EarningsMain() {
  const { t, intl } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const type = EARNING_TYPES.find((e) => e.id === pills.earnings) ?? EARNING_TYPES[0]!
  const chart = useStat(store, 'earnings/chart', earningsChartQuery(range, type.filter))
  const listKey = listKeyOf(`payouts/transactions:${type.id}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, transactionsLoader(store.call, range, type.tx))
  const d = (chart.data?.[type.key] ?? {}) as Raw
  return (
    <>
      <Pills items={EARNING_TYPES.map((e) => [e.id, e.label] as const)} active={type.id} onPick={(id) => setPill('earnings', id)} label={t('l.earnings')} />
      <div className="ofx-block">
        {chart.status === 'loading' ? (
          <Loading />
        ) : chart.status === 'error' ? (
          <ErrorLine message={chart.error} />
        ) : (
          <>
            <div className="ofx-chart-head">
              <strong>{money(d.total)}</strong>
              <span className="ofx-muted">{t('gross', { money: money(d.gross) })}</span>
              <Delta value={d.delta} />
            </div>
            <StatsChart main={{ points: d.chartAmount, kind: 'money', label: t('l.net') }} sub={{ points: d.chartCount, kind: 'count', label: t('l.transactions') }} />
          </>
        )}
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.data')}
          header={
            <TableHead cols="earnings">
              <span>{t('l.date')}</span>
              <span className="num">{t('l.amount')}</span>
              <span className="num">{t('l.fee')}</span>
              <span className="num">{t('l.net')}</span>
            </TableHead>
          }
        >
          {list.rows.map((row, i) => (
            <div key={String(row.id ?? i)} className="ofx-row">
              <div className="ofx-row-grid ofx-cols-earnings">
                <strong>{formatDateTime(row.createdAt, intl)}</strong>
                <span className="num">{money(row.amount)}</span>
                <span className="num">{money(row.fee)}</span>
                <strong className="num">{money(row.net)}</strong>
              </div>
              <div className="ofx-row-sub">
                <Description row={row} />
                <EarningStatus row={row} />
              </div>
            </div>
          ))}
        </ListBlock>
      </div>
    </>
  )
}

function PayoutList({ path, note, referral }: { path: string; note: string; referral: boolean }) {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const listKey = listKeyOf(path, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, path, withRange(range, { limit: PAGE_SIZE })))
  return (
    <>
      <div className="ofx-note">{note}</div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.data')}
          header={
            <TableHead cols="payouts">
              <span>{t('l.date')}</span>
              <span className="num">{t('l.invoice')}</span>
              <span className="num">{t('l.amount')}</span>
            </TableHead>
          }
        >
          {list.rows.map((row, i) => (
            <div key={String(row.id ?? i)} className="ofx-row">
              <div className="ofx-row-grid ofx-cols-payouts">
                <strong>{formatDateTime(row.createdAt, intl)}</strong>
                <span className="num">{String(row.invoiceId || '—')}</span>
                <strong className="num">{money(row.amount)}</strong>
              </div>
              <div className="ofx-row-sub ofx-payout-state">
                <PayoutState row={row} />
              </div>
              {String(row.state || '').toLowerCase() === 'approved' && Boolean(row.invoiceId) && (
                <div className="ofx-row-actions">
                  <a className="ofx-link-btn" target="_blank" rel="noopener noreferrer" title={t('invoiceHint')} href={invoiceUrl(row.invoiceId, referral)}>
                    {t('downloadInvoice')}
                  </a>
                </div>
              )}
            </div>
          ))}
        </ListBlock>
      </div>
    </>
  )
}

export function PayoutsMain() {
  const { t } = useStatsT()
  return (
    <>
      <div className="ofx-block">
        <PayoutChart path="payouts/chart" />
      </div>
      <PayoutList path="payouts/requests" note={t('note35')} referral={false} />
    </>
  )
}

export function ChargebacksMain() {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const listKey = listKeyOf('payouts/chargebacks', range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'payouts/chargebacks', withRange(range, { limit: PAGE_SIZE })))
  return (
    <>
      <div className="ofx-block">
        <PayoutChart path="payouts/chargebacks/chart" />
      </div>
      <div className="ofx-block ofx-list">
        <ListBlock
          listKey={listKey}
          list={list}
          empty={t('empty.data')}
          header={
            <TableHead cols="disputes">
              <span>{t('l.disputeDate')}</span>
              <span>{t('l.paymentDate')}</span>
              <span className="num">{t('l.amount')}</span>
              <span className="num">{t('l.feeNet')}</span>
            </TableHead>
          }
        >
          {list.rows.map((row, i) => {
            const p = (row.payment ?? {}) as Raw
            return (
              <div key={String(row.id ?? i)} className="ofx-row">
                <div className="ofx-row-grid ofx-cols-disputes">
                  <strong>{formatDateTime(row.createdAt, intl)}</strong>
                  <span>{formatDateTime(p.createdAt, intl)}</span>
                  <span className="num">{money(p.amount)}</span>
                  <span className="num">
                    {money(p.fee)}
                    <br />
                    <strong>{money(p.net)}</strong>
                  </span>
                </div>
                <div className="ofx-row-sub">
                  <Description row={p} />
                  <StatusIcon icon="undo-2" tone="reversed" label={t('chargedBack')} />
                </div>
              </div>
            )
          })}
        </ListBlock>
      </div>
    </>
  )
}

function ReferralUsers({ onlyPerformers }: { onlyPerformers: 0 | 1 }) {
  const { t, intl } = useStatsT()
  const { store, range } = useStats()
  const listKey = listKeyOf(`users/me/referrals:${onlyPerformers}`, range.startDate, range.endDate)
  const list = useStatList(store, listKey, offsetLoader(store.call, 'users/me/referrals', withRange(range, { onlyPerformers })))
  return (
    <div className="ofx-block ofx-list">
      <ListBlock listKey={listKey} list={list} empty={t('empty.data')}>
        {list.rows.map((row, i) => {
          const user = (row.user && typeof row.user === 'object' ? row.user : row) as Raw
          const amount = row.earnings ?? row.amount ?? row.totalSumm
          const date = row.createdAt || row.date || row.joinedAt
          return (
            <div key={String(row.id ?? i)} className="ofx-row">
              <div className="ofx-row-grid ofx-cols-user">
                <UserCell user={user} linked={false} />
                <span className="num">{date ? formatDate(date, intl) : ''}</span>
                <strong className="num">{amount !== undefined && amount !== null ? money(amount) : ''}</strong>
              </div>
            </div>
          )
        })}
      </ListBlock>
    </div>
  )
}

export function ReferralsMain() {
  const { t } = useStatsT()
  const { pills, setPill } = useStats()
  const pill = pills.referrals
  return (
    <>
      <Pills items={PILLS.referrals} active={pill} onPick={(id) => setPill('referrals', id)} label={t('l.referrals')} />
      {pill === 'earnings' ? (
        <>
          <div className="ofx-block">
            <PayoutChart path="payouts/referrals/chart" />
          </div>
          <PayoutList path="payouts/requests/referral" note={t('note7')} referral />
        </>
      ) : (
        <ReferralUsers key={pill} onlyPerformers={pill === 'creators' ? 1 : 0} />
      )}
    </>
  )
}
